import React, { useState, useRef, useEffect, useMemo } from 'react';
import ErrorsTableHeader from './ErrorsTableHeader';

// Высота фиксированного заголовка фильтров в пикселях - подберите значение в зависимости от реальной высоты
const FILTERS_HEIGHT = 70;

// Фиксированные ширины для колонок (можно настроить)
const DEFAULT_WIDTHS = {
  date: 140,  // Более широкая колонка для даты
  total: 100, // Колонка для итогов
  default: 130 // Дефолтная ширина для типов ошибок
};

/**
 * Компонент таблицы ошибок
 * @param {Object} props - Свойства компонента
 * @param {Array} props.errorTypes - Типы ошибок
 * @param {Object} props.dailyAggregatedData - Агрегированные данные по дням
 * @param {Object} props.errorMessages - Сообщения об ошибках для каждого типа
 */
const ErrorsTable = ({ errorTypes, dailyAggregatedData, errorMessages }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'asc' });
  const tableRef = useRef(null);
  const [headerWidth, setHeaderWidth] = useState(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  // Измеряем ширину таблицы при монтировании и изменении размеров
  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        setHeaderWidth(tableRef.current.offsetWidth);
      }
    };

    // Вызываем сразу при монтировании
    handleResize();
    
    // И добавляем обработчик изменения размера окна
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Обработчик скролла для определения, когда заголовок должен стать фиксированным
  useEffect(() => {
    const handleScroll = () => {
      if (tableRef.current) {
        const tableTop = tableRef.current.getBoundingClientRect().top;
        const tableBottom = tableRef.current.getBoundingClientRect().bottom;
        
        // Заголовок становится фиксированным, когда верх таблицы уходит за границу фильтров,
        // но только если нижняя часть таблицы всё еще видна
        setIsHeaderSticky(tableTop < FILTERS_HEIGHT && tableBottom > FILTERS_HEIGHT);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Обработчик клика по заголовку для сортировки
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Функция для сокращения текста
  const truncateErrorMessage = (message, maxWords = 3) => {
    if (!message) return '';
    const words = message.split(' ');
    if (words.length <= maxWords) return message;
    return `${words.slice(0, maxWords).join(' ')}...`;
  };

  // Сортировка данных с использованием useMemo для оптимизации
  const sortedDates = useMemo(() => {
    return Object.keys(dailyAggregatedData).sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      
      if (sortConfig.key === 'date') {
        return sortConfig.direction === 'asc' 
          ? dateA - dateB 
          : dateB - dateA;
      }
      
      // Сортировка по конкретному типу ошибки или всего
      const valueA = sortConfig.key === 'total' 
        ? dailyAggregatedData[a].total 
        : (dailyAggregatedData[a][sortConfig.key] || 0);
        
      const valueB = sortConfig.key === 'total' 
        ? dailyAggregatedData[b].total 
        : (dailyAggregatedData[b][sortConfig.key] || 0);
      
      if (valueA === valueB) {
        // При равных значениях сортируем по дате (новые первыми)
        return sortConfig.direction === 'asc' 
          ? dateA - dateB 
          : dateB - dateA;
      }
      
      return sortConfig.direction === 'asc' 
        ? valueA - valueB 
        : valueB - valueA;
    });
  }, [dailyAggregatedData, sortConfig.key, sortConfig.direction]);

  // Форматирование даты для отображения
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
  };

  // Расчет общего количества для каждого типа ошибки
  const getTotalForErrorType = (errorType) => {
    return Object.values(dailyAggregatedData).reduce(
      (sum, counts) => sum + (counts[errorType] || 0), 
      0
    );
  };

  // Общее количество всех ошибок
  const getGrandTotal = () => {
    return Object.values(dailyAggregatedData).reduce(
      (sum, counts) => sum + counts.total, 
      0
    );
  };

  // Создание тултипов для типов ошибок
  const getErrorTooltip = (errorType) => {
    const messages = errorMessages[errorType] || [];
    if (messages.length === 0) return `Errors of type: ${errorType}`;
    
    // Если есть примеры сообщений, показываем примеры
    if (messages.length <= 3) {
      return `Errors of type: ${errorType}\nExamples: ${messages.join(', ')}`;
    }
    
    // Если много сообщений, показываем их количество и первые три примера
    return `Errors of type: ${errorType}\n${messages.length} unique error messages\nExamples: ${messages.slice(0, 3).join(', ')}...`;
  };

  return (
    <div className="relative">
      {/* Фиксированный заголовок - появляется только при прокрутке */}
      {isHeaderSticky && (
        <div 
          className="fixed z-20 overflow-hidden bg-white shadow-md"
          style={{ 
            width: headerWidth ? `${headerWidth}px` : '100%',
            top: `${FILTERS_HEIGHT}px`, // Учитываем высоту фиксированных фильтров
            left: tableRef.current ? tableRef.current.getBoundingClientRect().left : 0
          }}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                {/* Заголовок колонки с датой */}
                <th 
                  className="px-6 py-3 border-b border-r border-gray-200 last:border-r-0 bg-gray-50"
                  style={{ width: `${DEFAULT_WIDTHS.date}px` }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider flex-grow cursor-pointer">
                      Date
                    </span>
                    <div 
                      className="cursor-pointer p-1 hover:bg-gray-200 rounded"
                      onClick={() => handleSort('date')}
                    >
                      {sortConfig.key === 'date' 
                        ? (sortConfig.direction === 'asc' 
                          ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="18 15 12 9 6 15"></polyline>
                            </svg>
                          : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>)
                        : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <polyline points="19 12 12 19 5 12"></polyline>
                          </svg>
                      }
                    </div>
                  </div>
                </th>
                
                {/* Заголовки колонок с типами ошибок */}
                {errorTypes.map(errorType => (
                  <th 
                    key={`sticky-${errorType}`}
                    className="px-6 py-3 border-b border-r border-gray-200 last:border-r-0 bg-gray-50"
                    style={{ width: `${DEFAULT_WIDTHS.default}px` }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider flex-grow cursor-pointer">
                        {truncateErrorMessage(errorType)}
                      </span>
                      <div 
                        className="cursor-pointer p-1 hover:bg-gray-200 rounded"
                        onClick={() => handleSort(errorType)}
                      >
                        {sortConfig.key === errorType 
                          ? (sortConfig.direction === 'asc' 
                            ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="18 15 12 9 6 15"></polyline>
                              </svg>
                            : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>)
                          : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50">
                              <line x1="12" y1="5" x2="12" y2="19"></line>
                              <polyline points="19 12 12 19 5 12"></polyline>
                            </svg>
                        }
                      </div>
                    </div>
                  </th>
                ))}
                
                {/* Заголовок колонки с общим количеством */}
                <th 
                  className="px-6 py-3 border-b border-r border-gray-200 last:border-r-0 bg-gray-50"
                  style={{ width: `${DEFAULT_WIDTHS.total}px` }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider flex-grow cursor-pointer">
                      Total
                    </span>
                    <div 
                      className="cursor-pointer p-1 hover:bg-gray-200 rounded"
                      onClick={() => handleSort('total')}
                    >
                      {sortConfig.key === 'total' 
                        ? (sortConfig.direction === 'asc' 
                          ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="18 15 12 9 6 15"></polyline>
                            </svg>
                          : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>)
                        : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <polyline points="19 12 12 19 5 12"></polyline>
                          </svg>
                      }
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
          </table>
        </div>
      )}

      {/* Основная таблица */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg shadow" ref={tableRef}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {/* Заголовок колонки с датой */}
              <ErrorsTableHeader 
                id="date"
                label="Date"
                onSort={handleSort}
                sortConfig={sortConfig}
                width={DEFAULT_WIDTHS.date}
              />
              
              {/* Заголовки колонок с типами ошибок */}
              {errorTypes.map(errorType => (
                <ErrorsTableHeader 
                  key={errorType}
                  id={errorType}
                  label={truncateErrorMessage(errorType)}
                  onSort={handleSort}
                  sortConfig={sortConfig}
                  tooltipContent={getErrorTooltip(errorType)}
                  width={DEFAULT_WIDTHS.default}
                />
              ))}
              
              {/* Заголовок колонки с общим количеством */}
              <ErrorsTableHeader 
                id="total"
                label="Total"
                onSort={handleSort}
                sortConfig={sortConfig}
                width={DEFAULT_WIDTHS.total}
              />
            </tr>
          </thead>
          
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Строки с данными по дням */}
            {sortedDates.map(date => (
              <tr key={date} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ width: `${DEFAULT_WIDTHS.date}px` }}>
                  {formatDate(date)}
                </td>
                {errorTypes.map(errorType => (
                  <td 
                    key={`${date}-${errorType}`} 
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center"
                    style={{ width: `${DEFAULT_WIDTHS.default}px` }}
                  >
                    {dailyAggregatedData[date][errorType] || 0}
                  </td>
                ))}
                <td 
                  className="px-6 py-4 whitespace-nowrap text-sm font-semibold bg-gray-50"
                  style={{ width: `${DEFAULT_WIDTHS.total}px` }}
                >
                  {dailyAggregatedData[date].total}
                </td>
              </tr>
            ))}
            
            {/* Итоговая строка */}
            <tr className="bg-gray-100 font-bold">
              <td 
                className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                style={{ width: `${DEFAULT_WIDTHS.date}px` }}
              >
                Total
              </td>
              {errorTypes.map(errorType => (
                <td 
                  key={`total-${errorType}`} 
                  className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center"
                  style={{ width: `${DEFAULT_WIDTHS.default}px` }}
                >
                  {getTotalForErrorType(errorType)}
                </td>
              ))}
              <td 
                className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-200"
                style={{ width: `${DEFAULT_WIDTHS.total}px` }}
              >
                {getGrandTotal()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ErrorsTable;